// @flow
import React from 'react';
import moment from 'moment';
import { useForm, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import {
  Stack,
  Tooltip,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Banner } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import { AlertInfoIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import SingleSelect from '../../../components/Forms/SingleSelect';
import DatePickerCL from '../../../components/Forms/DatePickerCL';
import useIntl from '../../../hooks/useIntl';
import messages from './messages';
import { useCreateDraftUnitTransfer } from './hooks';

type Mode = 'start' | 'edit';

type FormValues = {
  transferReasonId: string,
  transferDate: string,
};

type Props = {
  open: boolean,
  onClose: () => void,
  organizationId: string,
  propertyId: string,
  householdId: string,
  transferReasonOptions: Array<{ value: string, text: string }>,
  onConfirm?: () => void,
  mode?: Mode,
  initialValues?: FormValues,
  onSave?: (values: FormValues) => Promise<void>,
  isSaving?: boolean,
  // ENG-31072: certification copy is only accurate when the transfer actually involves
  // certifications. Both default to true so an unwired caller keeps the fuller (safer) wording.
  hasAffordablePrograms?: boolean,
  hasTransferCertifications?: boolean,
  // ENG-30316: edit mode only — the earliest transfer date the assigned unit allows
  // ('YYYY-MM-DD'), i.e. the day after its prior household moved out. The start modal never
  // receives this: no unit has been chosen yet, so there is nothing to restrict against, and
  // per-unit enforcement happens on the Manage Units screen instead.
  minTransferDate?: string | null,
};

function StartTransferModal({
  open,
  onClose,
  organizationId,
  propertyId,
  householdId,
  transferReasonOptions,
  onConfirm,
  mode = 'start',
  initialValues,
  onSave,
  isSaving = false,
  hasAffordablePrograms = true,
  hasTransferCertifications = true,
  minTransferDate = null,
}: Props) {
  const intl = useIntl();
  const isEdit = mode === 'edit';
  const emptyValues: FormValues = { transferReasonId: '', transferDate: '' };
  const seededValues: FormValues =
    isEdit && initialValues ? initialValues : emptyValues;

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isSubmitting },
  } = useForm({
    mode: 'onTouched',
    defaultValues: seededValues,
  });

  // Reopening in edit mode should reflect the latest persisted values.
  React.useEffect(() => {
    if (open && isEdit && initialValues) {
      reset(initialValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ENG-30316: the occupancy message appears ONLY when the current value actually falls inside
  // the prior occupancy — never as a standing note. It is phrased about "the entered transfer
  // date", so showing it against a valid one accuses the user of a mistake they did not make.
  // `minDate` stops them PICKING such a date, but typing one, or opening a transfer created
  // before this rule existed, still reaches this state.
  const selectedTransferDate = useWatch({ control, name: 'transferDate' });
  const transferDateBeforeMinimum = Boolean(
    minTransferDate &&
      selectedTransferDate &&
      moment(selectedTransferDate).isBefore(moment(minTransferDate), 'day'),
  );

  const errorMessage = intl.formatMessage(messages.errorCreatingDraft);
  const createDraftMutation = useCreateDraftUnitTransfer(
    organizationId,
    propertyId,
    householdId,
    () => {
      reset();
      onClose();
      if (onConfirm) {
        onConfirm();
      }
    },
    errorMessage,
  );

  const onSubmit = async (values: FormValues) => {
    if (isEdit) {
      if (onSave) {
        await onSave({
          transferReasonId: values.transferReasonId,
          transferDate: values.transferDate,
        });
      }
      return;
    }
    createDraftMutation.mutate({
      transferDate: values.transferDate,
      transferReasonId: values.transferReasonId,
    });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const inFlight = isEdit ? isSaving : createDraftMutation.isLoading;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={<FormattedMessage {...messages.title} />}
      actionsProps={[
        {
          children: <FormattedMessage {...messages.cancel} />,
          onClick: handleClose,
          variant: 'text',
        },
        {
          children: <FormattedMessage {...messages.confirm} />,
          submitButton: true,
          // ENG-30316: `transferDateBeforeMinimum` is not covered by `isValid` — the only rule on
          // the field is `required`, and MUI's minDate blocks picking an early date but not being
          // seeded with one. Without this the user can submit the very date the helper text says
          // is invalid.
          disabled:
            !isValid || transferDateBeforeMinimum || isSubmitting || inFlight,
          isSubmitting: isSubmitting || inFlight,
          onClick: handleSubmit(onSubmit),
        },
      ]}
      ModalProps={{
        sx: {
          '.MuiPaper-root': {
            maxWidth: '600px',
            width: '100%',
          },
        },
      }}
    >
      <Stack direction="column" spacing={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems="flex-start"
        >
          {/* Reason for Transfer */}
          <Stack direction="column" spacing={0.5} sx={{ flex: 1 }}>
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ minHeight: 24 }}
            >
              <Typography variant="labelMedium">
                <FormattedMessage {...messages.transferReasonLabel} />
              </Typography>
            </Stack>
            <SingleSelect
              name="transferReasonId"
              control={control}
              rules={{
                required: intl.formatMessage(messages.transferReasonRequired),
              }}
              options={transferReasonOptions}
              placeholder={intl.formatMessage(messages.transferReasonLabel)}
              fullWidth
            />
          </Stack>

          {/* Transfer Date */}
          <Stack direction="column" spacing={0.5} sx={{ flex: 1 }}>
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ minHeight: 24 }}
            >
              <Typography variant="labelMedium">
                <FormattedMessage {...messages.transferDateLabel} />
              </Typography>
              <Tooltip
                // ENG-30316: in edit mode the picker actually blocks days, so the info icon
                // explains which ones. Gated on `minTransferDate` too — with no prior occupancy
                // nothing is greyed out and the sentence would simply be untrue.
                title={intl.formatMessage(
                  isEdit && minTransferDate
                    ? messages.transferDateOccupiedHint
                    : messages.transferDateTooltip,
                )}
                placement="top"
                variant="light"
              >
                <span>
                  <AlertInfoIcon sx={{ fontSize: 16, cursor: 'help' }} />
                </span>
              </Tooltip>
            </Stack>
            <DatePickerCL
              name="transferDate"
              control={control}
              rules={{
                required: intl.formatMessage(messages.transferDateRequired),
              }}
              fullWidth
              // ENG-30316: MUI greys every day before minDate.
              minDate={minTransferDate || undefined}
              helperText={
                transferDateBeforeMinimum
                  ? intl.formatMessage(messages.transferDateOccupiedError)
                  : undefined
              }
            />
          </Stack>
        </Stack>

        {/* Transfer date-change warning (edit mode) — component-library warning banner */}
        {isEdit && (
          <Banner
            status="warning"
            message={
              <>
                <Typography
                  variant="bodySmall"
                  component="span"
                  sx={{ display: 'block', color: 'warning.main' }}
                >
                  <FormattedMessage {...messages.dateChangeWarningHeader} />
                </Typography>
                <Typography
                  variant="bodySmall"
                  component="span"
                  sx={{
                    display: 'block',
                    color: 'warning.main',
                    '&::before': { content: '"• "' },
                  }}
                >
                  <FormattedMessage {...messages.dateChangeWarningBullet1} />
                </Typography>
                {hasTransferCertifications && (
                  <Typography
                    variant="bodySmall"
                    component="span"
                    sx={{
                      display: 'block',
                      color: 'warning.main',
                      '&::before': { content: '"• "' },
                    }}
                  >
                    <FormattedMessage {...messages.dateChangeWarningBullet2} />
                  </Typography>
                )}
              </>
            }
          />
        )}

        {/* Info note (start mode only) */}
        {!isEdit && (
          <Banner
            status="info"
            message={intl.formatMessage(
              hasAffordablePrograms
                ? messages.infoNote
                : messages.infoNoteConventional,
            )}
          />
        )}
      </Stack>
    </Modal>
  );
}

export default StartTransferModal;
